<template>
	<Dialog
		:dialog-visible="dialogVisible"
		:title="''"
		:remove-footer="true"
		:persistent-dialog="true"
		:options="{ width: '780px', fullHeight: true }"
		@cancel="handleClose">
		<template #dialog-header="{ titleId }">
			<div class="d-flex flex-column ga-2 w-100">
				<div class="d-flex align-center justify-space-between w-100 ga-2">
					<div class="d-flex align-center ga-3" style="min-width: 0; overflow: hidden">
						<v-avatar color="accent" size="40" class="flex-shrink-0">
							<v-icon color="primary" size="20">mdi-file-document-outline</v-icon>
						</v-avatar>
						<div style="min-width: 0">
							<div :id="titleId" class="text-body-1 font-weight-bold text-truncate">{{ sheetName || "New Sheet" }}</div>
							<div class="text-caption text-grey text-truncate">{{ createdLabel }}</div>
						</div>
					</div>
					<div class="d-flex align-center ga-2 flex-shrink-0">
						<!-- LSP-16769: on sm/xs the Save/Share buttons drop to a centered row below (see
						     next block) so they stop squeezing the title; Close stays pinned top-right. -->
						<template v-if="!isViewOnly && display.mdAndUp.value">
							<v-btn color="primary" variant="flat" rounded="xl" :loading="saving" @click="handleSave">Save</v-btn>
							<v-btn color="primary" variant="outlined" rounded="xl" :loading="saving" @click="handleShareClick">Share</v-btn>
						</template>
						<v-btn icon="icon-close-medium" variant="text" density="comfortable" @click="handleClose" />
					</div>
				</div>
				<div v-if="!isViewOnly && display.smAndDown.value" class="d-flex justify-center ga-2 mt-3 mb-n1">
					<v-btn color="primary" variant="flat" rounded="xl" :loading="saving" @click="handleSave">Save</v-btn>
					<v-btn color="primary" variant="outlined" rounded="xl" :loading="saving" @click="handleShareClick">Share</v-btn>
				</div>
			</div>
		</template>
		<template #dialog-body>
			<div v-if="loadingSheet" class="d-flex justify-center align-center pa-12">
				<v-progress-circular indeterminate color="primary" size="48" />
			</div>
			<v-form v-else ref="form" v-model="isFormValid" class="sheet-canvas">
				<!-- Title + Subtitle card -->
				<div class="sheet-card">
					<!-- Both fields are CharacterLimitedTextArea, not CharacterLimitedTextField, purely so a
						 long value stays READABLE (LSP-16763). They are still semantically single-line — see
						 normalizeSingleLineText / onSingleLineEnter below, which live HERE and not in
						 CharacterLimitedTextArea on purpose: this is the only one of its seven consumers that
						 wants a single-line value, and the shared component is the most break-it-tested pair in
						 the app, so a bug in the normalizer would reach AddComment and three settings screens for
						 no gain. If a SECOND consumer ever needs this, push it down to a `single-line` prop applied
						 in that component's own updateTextareaValue, where the composition state already lives. A single-line <input> cannot wrap,
						 and iOS Safari will not touch-scroll an input's horizontal overflow, so on an iPad the
						 tail of an existing long title/subtitle was simply unreachable — and unreachable for
						 good on a view-only sheet, where `readonly` also removes the caret drag that is the
						 only other way to pan an <input>. `rows="1" auto-grow` keeps the collapsed field one
						 line tall for the common short title, then grows it as the text wraps; the VTextarea
						 `maxHeight: 300` global default (src/plugins/vuetify.js) caps that growth and makes
						 the textarea itself scroll past it. No `no-resize` is needed: Vuetify already sets
						 `v-textarea--no-resize` from `auto-grow` alone (VTextarea.js), which is why the notes
						 field below passes `auto-grow` on its own.
						 The two `min-height: unset` overrides these fields used to carry are gone on purpose —
						 see the scoped style block at the bottom of this file for why.

						 FIELD_LENGTHS.SHEET_TITLE / SHEET_SUBTITLE (255 code points each) are enforced by
						 the field's :max-length (LSP-16852). No server-unit backstop needed:
						 contact-api's updateSheet (sheets.ts:232 title, :236 subtitle) validates with
						 countGraphemes, not raw UTF-16 — graphemes are always <= code points, so a
						 code-point-valid client value can never exceed what a grapheme-counting server
						 accepts (§3.3/§10 of the sweep plan). :trim="false" on both fields is load-bearing,
						 not incidental (same reasoning as the notes field below): sheets.ts's
						 countGraphemes(body.name)/countGraphemes(body.subtitle) measure the RAW value, and
						 persistSheet() below posts sheetName.value/sheetSubtitle.value with no .trim() —
						 so the client counter/rule must judge the same untrimmed string the server measures,
						 or a title/subtitle padded with whitespace right at the limit could pass client-side
						 and still be rejected on save (LSP-16852 review). -->
					<CharacterLimitedTextArea
						v-model="sheetName"
						field-noun="sheet title"
						placeholder="Untitled Sheet"
						variant="plain"
						density="compact"
						hide-details="auto"
						rows="1"
						auto-grow
						:max-length="FIELD_LENGTHS.SHEET_TITLE"
						:trim="false"
						class="sheet-title-input"
						:readonly="isViewOnly"
						@update:focused="titleFocused = $event"
						@keydown.enter="onSingleLineEnter"
						@paste="onSingleLineInsert"
						@drop="onSingleLineInsert">
						<template v-if="!isViewOnly && !titleFocused" #append-inner>
							<v-icon
								v-test="'sheet-title-edit-affordance'"
								class="sheet-title-edit-affordance"
								size="18"
								color="primary"
								aria-hidden="true">
								mdi-pencil
							</v-icon>
						</template>
					</CharacterLimitedTextArea>
					<!-- No server-unit backstop needed here either — see the title field's comment above,
						 which also covers why :trim="false" is required on this field. -->
					<CharacterLimitedTextArea
						v-model="sheetSubtitle"
						field-noun="sheet subtitle"
						placeholder="Add a subtitle..."
						variant="plain"
						density="compact"
						hide-details="auto"
						rows="1"
						auto-grow
						:max-length="FIELD_LENGTHS.SHEET_SUBTITLE"
						:trim="false"
						class="sheet-subtitle-input"
						:readonly="isViewOnly"
						@update:focused="subtitleFocused = $event"
						@keydown.enter="onSingleLineEnter"
						@paste="onSingleLineInsert"
						@drop="onSingleLineInsert">
						<template v-if="!isViewOnly && !subtitleFocused" #append-inner>
							<v-icon
								v-test="'sheet-subtitle-edit-affordance'"
								class="sheet-subtitle-edit-affordance"
								size="18"
								color="primary"
								aria-hidden="true">
								mdi-pencil
							</v-icon>
						</template>
					</CharacterLimitedTextArea>

					<!-- Project Information -->
					<div class="sheet-section-wrapper">
						<div v-if="showDetails">
							<div data-section-header-row="showDetails" class="sheet-section-header-row">
								<div data-section-heading="showDetails" class="section-heading mb-0">
									<div data-section-accent="showDetails" class="section-accent-bar"></div>
									<v-btn
										v-if="!isViewOnly"
										data-section-toggle="showDetails"
										:icon="'mdi-eye'"
										variant="text"
										density="compact"
										size="28"
										class="section-visibility-toggle is-visible"
										@click.stop="toggleSection('showDetails')" />
									<span>Project Information</span>
								</div>
							</div>
							<div class="details-row" :class="{ 'no-data': !hasAnyDetails }">
								<div v-if="hasAnyDetails" class="details-columns">
									<div v-if="contactDisplay" class="detail-cell">
										<div class="detail-label">CONTACT</div>
										<div class="detail-value">
											<v-icon size="16" color="grey" class="mr-1">mdi-account-outline</v-icon>
											<span class="text-truncate">{{ contactDisplay }}</span>
										</div>
									</div>
									<div v-if="sheetData?.createdAt" class="detail-cell">
										<div class="detail-label">DATE</div>
										<div class="detail-value">
											<v-icon size="16" color="grey" class="mr-1">mdi-calendar-outline</v-icon>
											<span class="text-truncate">{{ formatDate(sheetData.createdAt) }}</span>
										</div>
									</div>
									<div v-if="sheetData?.address" class="detail-cell">
										<div class="detail-label">LOCATION</div>
										<div class="detail-value">
											<v-icon size="16" color="grey" class="mr-1">mdi-map-marker-outline</v-icon>
											<span class="text-truncate">{{ sheetData.address.formattedAddress }}</span>
										</div>
									</div>
								</div>
								<div v-else class="text-body-2 text-grey pa-3 text-center">No project details available.</div>
							</div>
						</div>
						<div v-else class="hidden-section-inline" @click="toggleSection('showDetails')">
							<div data-section-heading="showDetails" class="section-heading muted">
								<div data-section-accent="showDetails" class="section-accent-bar muted"></div>
								<v-btn
									v-if="!isViewOnly"
									data-section-toggle="showDetails"
									:icon="'mdi-eye-off'"
									variant="text"
									density="compact"
									size="28"
									class="section-visibility-toggle is-hidden" />
								<span>Project Information</span>
							</div>
							<span class="text-caption text-grey ml-4">Hidden from view</span>
						</div>
					</div>
				</div>

				<!-- Notes & Media sections (shared toggle pattern) -->
				<template v-for="section in toggleableSections" :key="section.key">
					<v-divider v-if="section.key === 'showGallery'" class="my-2" />

					<div class="sheet-section-wrapper">
						<div v-if="section.visible" :class="{ 'notes-section': section.key === 'showNotes' }">
							<!-- Shared visible section header -->
							<div :data-section-header-row="section.key" class="sheet-section-header-row">
								<div :data-section-heading="section.key" class="section-heading mb-0">
									<div :data-section-accent="section.key" class="section-accent-bar"></div>
									<v-btn
										v-if="!isViewOnly"
										:data-section-toggle="section.key"
										:icon="'mdi-eye'"
										variant="text"
										density="compact"
										size="28"
										class="section-visibility-toggle is-visible"
										@click.stop="toggleSection(section.key)" />
									<span>{{ section.visibleLabel }}</span>
								</div>
								<v-btn
									v-if="section.key === 'showGallery' && !isViewOnly"
									variant="text"
									density="compact"
									size="small"
									color="primary"
									@click="mediaPickerVisible = true">
									Add Media
								</v-btn>
							</div>

							<!-- Notes body -->
							<template v-if="section.key === 'showNotes'">
								<CharacterLimitedTextArea
									:model-value="sheetNotes"
									data-testid="sheet-notes-input"
									placeholder="Add notes..."
									rows="4"
									auto-grow
									:readonly="isViewOnly"
									:max-length="FIELD_LENGTHS.SHEET_NOTES"
									:trim="false"
									:over-limit-message="notesTooLongMessage"
									@update:model-value="onNotesChange"
									@keydown="onNotesKeydown" />
							</template>

							<!-- Media body -->
							<template v-if="section.key === 'showGallery'">
								<div v-if="mediaItems.length === 0" class="empty-media-state">
									<v-icon size="40" color="grey-lighten-1">mdi-image-multiple</v-icon>
									<div class="text-body-2 text-grey mt-2">No Media Selected</div>
								</div>
								<div v-else class="media-grid" @dragover.prevent="onDragOver" @drop="onDrop">
									<div
										v-for="(item, index) in mediaItems"
										:key="item.id"
										class="media-item"
										:class="{ 'opacity-30': dragState.dragId === item.id }"
										:data-media-id="item.id"
										:draggable="!isViewOnly"
										@dragstart="onDragStart(item.id, $event)"
										@dragend="onDragEnd(item.id)">
										<v-divider
											v-if="showDropIndicator(index, 'before')"
											vertical
											thickness="3"
											color="primary"
											class="border-opacity-100 drop-divider-before" />
										<div class="media-thumb-wrapper">
											<v-img :src="item.thumbnailUrl || item.mediaUrl" aspect-ratio="1" cover class="rounded-lg">
												<template #placeholder>
													<div class="d-flex align-center justify-center fill-height bg-grey-lighten-3">
														<v-progress-circular indeterminate size="20" color="grey" />
													</div>
												</template>
												<div
													v-if="item.mediaType === 'recording'"
													class="video-overlay d-flex align-center justify-center">
													<v-icon class="play-button">icon-play-solid</v-icon>
												</div>
											</v-img>
											<v-btn
												v-if="!isViewOnly"
												icon
												size="x-small"
												variant="flat"
												class="remove-media-btn"
												@click.stop="removeMedia(index)">
												<v-icon size="small">icon-close-medium</v-icon>
											</v-btn>
											<v-icon
												v-if="!isViewOnly"
												class="drag-handle"
												size="small"
												@touchstart.prevent="onTouchStart(item.id)"
												@touchmove="onTouchMove($event)"
												@touchend="onTouchEnd(item.id)"
												@touchcancel="onTouchCancel(item.id)">
												icon-grid-2x3
											</v-icon>
										</div>
										<v-divider
											v-if="showDropIndicator(index, 'after')"
											vertical
											thickness="3"
											color="primary"
											class="border-opacity-100 drop-divider-after" />
									</div>
								</div>
							</template>
						</div>

						<!-- Shared hidden section toggle -->
						<div v-else class="hidden-section-inline" @click="toggleSection(section.key)">
							<div :data-section-heading="section.key" class="section-heading muted">
								<div :data-section-accent="section.key" class="section-accent-bar muted"></div>
								<v-btn
									v-if="!isViewOnly"
									:data-section-toggle="section.key"
									:icon="'mdi-eye-off'"
									variant="text"
									density="compact"
									size="28"
									class="section-visibility-toggle is-hidden" />
								<span>{{ section.hiddenLabel }}</span>
							</div>
							<span class="text-caption text-grey ml-4">Hidden from view</span>
						</div>
					</div>
				</template>
			</v-form>

			<!-- Media Picker Dialog -->
			<Dialog
				:dialog-visible="mediaPickerVisible"
				title="Add Media"
				agree-text="Add Selected"
				cancel-text="Cancel"
				:disable-agree-button="pickerSelected.length === 0"
				:agree-loading="false"
				:options="{ width: '600px', fullHeight: true }"
				@agree="addSelectedMedia"
				@cancel="mediaPickerVisible = false">
				<template #dialog-body>
					<div v-if="loadingProjectMedia" class="d-flex justify-center pa-6">
						<v-progress-circular indeterminate color="primary" size="32" />
					</div>
					<div v-else-if="availableMedia.length === 0" class="text-center pa-6 text-grey">
						No media available in this project.
					</div>
					<div v-else class="media-picker-grid">
						<div
							v-for="item in availableMedia"
							:key="item.id"
							class="media-picker-item"
							:class="{ selected: pickerSelected.includes(item.id) }"
							@click="togglePickerItem(item.id)">
							<v-img :src="item.thumbnailUrl || item.mediaUrl" aspect-ratio="1" cover class="rounded-lg">
								<template #placeholder>
									<div class="d-flex align-center justify-center fill-height bg-grey-lighten-3">
										<v-icon color="grey">mdi-image</v-icon>
									</div>
								</template>
								<div v-if="item.mediaType === 'recording'" class="video-overlay d-flex align-center justify-center">
									<v-icon class="play-button">icon-play-solid</v-icon>
								</div>
							</v-img>
							<v-icon v-if="pickerSelected.includes(item.id)" class="picker-check" color="primary" size="20">
								mdi-check-circle
							</v-icon>
						</div>
					</div>
				</template>
			</Dialog>

			<!-- Share Sheet Dialog -->
			<ShareSheetDialog
				:dialog-visible="shareDialogVisible"
				:sheet-id="props.sheet?.id || ''"
				:sheet-url="sheetPublicUrl"
				:sheet-name="sheetName"
				@cancel="shareDialogVisible = false" />
		</template>
	</Dialog>
</template>

<script lang="ts" setup>
	import { ref, useTemplateRef, watch, computed, getCurrentInstance, onBeforeUnmount } from "vue";
	import { useDisplay } from "vuetify";
	import type { VForm } from "vuetify/components";
	import { useToast } from "vue-toastification";
	import Dialog from "@/components/Dialog.vue";
	import ShareSheetDialog from "@/components/ShareSheetDialog.vue";
	import { getCustomerDisplayNames } from "@/formatters/customers";
	import { toSplicedCompat } from "@/helpers/arrayCompat";
	import { mapProjectMediaForSheetPicker, type ProjectSheetMediaItem } from "@/helpers/sheetMedia";
	import { fetchAllProjectSnapshots } from "@/helpers/projectMediaPaging";
	import { FIELD_LENGTHS } from "@/helpers/Validation";
	import { calculateTimeout, getMessageFromError } from "@/helpers/utilities";
	import { isWithinCharacterLimit } from "@/composables/useCharacterLimit";
	import { UNSAVED_CHANGES_DIALOG_OPTIONS, UNSAVED_CHANGES_DIALOG_TEXT } from "@/composables/useUnsavedChangesGuard";
	import { useLatestRequest } from "@/composables/useLatestRequest";
	import api from "@/api";

	const display = useDisplay();

	interface SheetMediaItem {
		id: string;
		mediaId: string;
		mediaType: "snapshot" | "recording";
		mediaUrl: string;
		thumbnailUrl: string;
		notes: string | null;
		createdAt: string;
		createdBy: string | null;
		sortOrder: number;
	}

	interface SheetContact {
		id: string;
		firstname: string | null;
		lastname: string | null;
		companyName: string | null;
		EmailAddresses: Array<{ id: string; emailAddress: string }>;
		PhoneNumbers: Array<{ id: string; phoneNumber: string }>;
	}

	interface SheetAddress {
		formattedAddress: string;
		latitude: number | null;
		longitude: number | null;
	}

	interface SheetPublicData {
		id: string;
		name: string | null;
		subtitle: string | null;
		notes: string | null;
		includeDetails: boolean;
		includeCaptions: boolean;
		showDetails: boolean;
		showNotes: boolean;
		showGallery: boolean;
		createdAt: string | null;
		contacts: SheetContact[];
		address: SheetAddress | null;
		media: SheetMediaItem[];
	}

	interface Props {
		dialogVisible: boolean;
		sheet: any;
		projectId: string;
		isViewOnly: boolean;
	}

	type DragState = {
		dragId: string | null;
		dropId: string | null;
		dropAt: "before" | "after" | null;
	};

	const props = defineProps<Props>();
	const emit = defineEmits<{
		(e: "close"): void;
		(e: "saved"): void;
	}>();

	const instance = getCurrentInstance();
	const toast = useToast();

	function notifyError(err: unknown, fallback: string) {
		const message = getMessageFromError(err) || fallback;
		toast.error(message, { timeout: calculateTimeout(message) });
	}

	// Sheet state
	const sheetData = ref<SheetPublicData | null>(null);
	const sheetNameValue = ref("");
	const sheetSubtitleValue = ref("");
	const sheetNotes = ref("");

	/**
	 * The title and subtitle render in a textarea so a long value wraps and scrolls instead of being
	 * clipped (LSP-16763), but they are still single-line VALUES. Collapse every run of line breaks to
	 * one space so a newline can never reach the model, the counter, the dirty snapshot, or the save
	 * payload — the paths a keystroke guard cannot cover: paste, macOS Dictation, and the load below.
	 * A space rather than "" so two words do not silently fuse into one.
	 *
	 * Normalizing on LOAD as well as on edit is deliberate. Every surface that renders a stored
	 * title/subtitle — the dialog header chip above, the sheet list, the public sheet — is ordinary
	 * HTML, which already collapses a newline to a space, so a legacy value carrying one has never
	 * displayed as two lines anywhere. Letting it through here is the only place it would, and it would
	 * do so in a field the user cannot type a newline into.
	 */
	function normalizeSingleLineText(value: unknown): string {
		// Non-strings fail closed to "" rather than being coerced (break-it round 1). This setter is the
		// only writer of the backing refs, so it also runs on load — and getSheetPublic is untyped at
		// runtime. Before the textarea swap a junk `name` was inert (the ref just held it and Vuetify
		// stringified it); now it reaches .replace(), so an API returning a number/object/array for a
		// title would throw during load and leave the editor stuck on its spinner. Showing the
		// "Untitled Sheet" placeholder beats both crashing and inventing a title like "42" that the
		// next Save would then persist.
		if (typeof value !== "string") return "";
		// Beyond CR and LF because the value now lives in a textarea: CSS Text treats VT, FF, NEL,
		// LINE SEPARATOR and PARAGRAPH SEPARATOR as forced line breaks too, so leaving them in would
		// render a "single-line" title across two lines — the exact invariant this function exists for.
		// eslint-disable-next-line no-control-regex -- VT and FF are exactly what this must match.
		return value.replace(/[\r\n\u000B\u000C\u0085\u2028\u2029]+/gu, " ");
	}

	const sheetName = computed({
		get: () => sheetNameValue.value,
		set: (value: string) => {
			sheetNameValue.value = normalizeSingleLineText(value);
		}
	});

	const sheetSubtitle = computed({
		get: () => sheetSubtitleValue.value,
		set: (value: string) => {
			sheetSubtitleValue.value = normalizeSingleLineText(value);
		}
	});

	/**
	 * Enter is a no-op in the title/subtitle textareas: without this the keystroke would insert a
	 * newline that normalizeSingleLineText immediately collapses to a space, which reads as a stray
	 * space plus a caret jump to the end of the field. Nothing else is swallowed — the surrounding
	 * v-form has no submit handler and Save/Share are buttons, so Enter did nothing in these fields
	 * when they were <input>s either.
	 *
	 * `isComposing` is the escape hatch that keeps IME and dictation working: those commit their
	 * candidate WITH Enter, so preventing it would strip the commit key out from under them.
	 */
	/**
	 * Normalize line breaks BEFORE they reach the DOM, rather than reconciling afterwards.
	 *
	 * The computed setters below still normalize the model, but they cannot fix what the field is
	 * DISPLAYING. CharacterLimitedTextArea renders its own buffer and re-syncs it from the parent
	 * through a change-gated watch, so when a pasted draft normalizes onto the string the model already
	 * holds — pasting "Kitchen\nReno" over a title that already reads "Kitchen Reno" — the assignment
	 * is a same-value no-op, the watch never fires, and the field goes on showing the raw value across
	 * two lines. That is the LSP-16763 symptom itself (server review of fcda72866c, finding 1).
	 *
	 * Fixing that inside the shared component by force-syncing after every input was tried and reverted:
	 * it cannot tell a FINAL transform (this one) from an IN-FLIGHT one, and it broke ContactSupportDialog,
	 * whose `v-model.trim` made every typed space vanish before a second word could be started
	 * (Bugbot, b5770adf88). Sanitising at the entry point avoids the question — the textarea never holds
	 * a line break, so display and model cannot disagree in the first place.
	 *
	 * Keystrokes are covered by onSingleLineEnter, and dictation by the component's own compositionend,
	 * which re-syncs unconditionally. That leaves paste and drop, which is what this handles.
	 */
	function onSingleLineInsert(event: ClipboardEvent | DragEvent) {
		const target = event.target;
		if (!(target instanceof HTMLTextAreaElement)) return;
		// Bail on a view-only sheet. `readonly` only stops the BROWSER's default insert; setRangeText is a
		// scripted write and lands regardless, so without this a viewer could paste into the title of a
		// sheet they cannot edit and dirty it (Bugbot, 38ef02bc57). Read off the element rather than
		// props.isViewOnly so this can never disagree with what the field is actually rendering, and
		// return WITHOUT preventDefault so the native no-op stays the browser's to perform.
		if (target.readOnly || target.disabled) return;
		// Read the payload without `instanceof DragEvent`: that global is not defined in every environment
		// this component is exercised in, and referencing a missing global throws before the guard can run.
		const transfer = (event as ClipboardEvent).clipboardData ?? (event as DragEvent).dataTransfer;
		const incoming = transfer?.getData("text/plain");
		// Nothing to normalize: let the browser handle it, so undo history and drop semantics stay native.
		if (!incoming || incoming === normalizeSingleLineText(incoming)) return;

		event.preventDefault();
		const start = target.selectionStart ?? target.value.length;
		const end = target.selectionEnd ?? start;
		target.setRangeText(normalizeSingleLineText(incoming), start, end, "end");
		// The field owns its rendered buffer, so tell it to re-read the DOM we just corrected.
		target.dispatchEvent(new Event("input", { bubbles: true }));
	}

	function onSingleLineEnter(event: KeyboardEvent) {
		if (event.isComposing) return;
		event.preventDefault();
	}
	// Plain arrays — not reactive; UI never renders these directly.
	const notesUndoStack: string[] = [];
	const notesRedoStack: string[] = [];

	function onNotesChange(value: string) {
		notesUndoStack.push(sheetNotes.value);
		if (notesUndoStack.length > 500) notesUndoStack.shift();
		notesRedoStack.length = 0;
		sheetNotes.value = value;
	}

	function onNotesKeydown(event: KeyboardEvent) {
		if (props.isViewOnly) return;
		const isMod = event.ctrlKey || event.metaKey;
		if (!isMod || event.key.toLowerCase() !== "z") return;
		if (event.shiftKey) {
			const next = notesRedoStack.pop();
			if (next === undefined) return;
			event.preventDefault();
			notesUndoStack.push(sheetNotes.value);
			sheetNotes.value = next;
		} else {
			const previous = notesUndoStack.pop();
			if (previous === undefined) return;
			event.preventDefault();
			notesRedoStack.push(sheetNotes.value);
			sheetNotes.value = previous;
		}
	}

	const titleFocused = ref(false);
	const subtitleFocused = ref(false);
	const isFormValid = ref<boolean | null>(null);
	const form = useTemplateRef<InstanceType<typeof VForm>>("form");
	// FIELD_LENGTHS.SHEET_NOTES (5000 code points) is now enforced by CharacterLimitedTextArea's
	// :max-length (LSP-16852), paired with notesTooLongMessage via :over-limit-message so the visible
	// field keeps its custom copy. :trim="false" on the field is load-bearing, not incidental: notes
	// has always counted and saved whitespace-only input VERBATIM (see "treats whitespace-only and
	// empty notes as valid while counting whitespace without trimming" below), matching contact-api's
	// own untrimmed `countGraphemes(body.notes)` — F1's default trim:true would silently start
	// stripping leading/trailing whitespace before counting, showing "0" for a whitespace-only note
	// the user actually typed. No server-unit backstop needed: Gallery.notes is DataTypes.TEXT
	// (unbounded), and contact-api's updateSheet (sheets.ts:228) validates notes with countGraphemes,
	// not raw UTF-16 or code points — graphemes are always <= code points, so a code-point-valid
	// client value can never exceed what a grapheme-counting server accepts (mirrors the
	// title/subtitle reasoning above).
	const notesTooLongMessage = "Please enter a shorter note.";
	// Called directly from validateSheetForm() below rather than bound as a field rule: when the Notes
	// section is collapsed the field unmounts and takes its rules out of the v-form with it, so the
	// limit would go unchecked on Save. CharacterLimitedTextArea's own :max-length rule covers the
	// field while it IS mounted. `trim: false` matches the field's own `:trim="false"` — notes has
	// always counted and saved whitespace-only input verbatim (see the comment above).
	const isSheetNotesWithinLimit = (value: string) => isWithinCharacterLimit(value, FIELD_LENGTHS.SHEET_NOTES, false);
	const showDetails = ref(true);
	const showNotes = ref(true);
	const showGallery = ref(true);
	const mediaItems = ref<SheetMediaItem[]>([]);
	const saving = ref(false);

	// Snapshot of initial state for dirty comparison
	const savedSnapshot = ref("");

	function buildSnapshot(): string {
		return JSON.stringify({
			name: sheetName.value,
			subtitle: sheetSubtitle.value,
			notes: sheetNotes.value,
			showDetails: showDetails.value,
			showNotes: showNotes.value,
			showGallery: showGallery.value,
			mediaIds: mediaItems.value.map((item) => item.mediaId)
		});
	}

	const isDirty = computed(() => savedSnapshot.value !== "" && buildSnapshot() !== savedSnapshot.value);
	const loadingSheet = ref(false);
	const sheetLoadRequest = useLatestRequest();

	// Drag state
	const dragState = ref<DragState>({ dragId: null, dropId: null, dropAt: null });
	// Pending drop-target pointer, applied at most once per animation frame (LSP-18065).
	// Declared before the dialog-visibility watch below, which cleans them up on close.
	let pendingDropPointer: { clientX: number; clientY: number; grid: HTMLElement } | null = null;
	let dropUpdateFrame: number | null = null;

	// Share dialog state
	const shareDialogVisible = ref(false);
	const sheetPublicUrl = computed(() => {
		if (!props.sheet?.id || !props.sheet?.accessToken) return "";
		const adminBaseUrl = (window as any).env?.VITE_APP_URL || window.location.origin;
		return `${adminBaseUrl}/sheet/${props.sheet.id}/${props.sheet.accessToken}`;
	});

	// Media picker state
	const mediaPickerVisible = ref(false);
	const loadingProjectMedia = ref(false);
	const availableMedia = ref<ProjectSheetMediaItem[]>([]);
	const pickerSelected = ref<string[]>([]);

	const createdLabel = computed(() => {
		if (!sheetData.value?.createdAt) return "New sheet";
		const date = new Date(sheetData.value.createdAt);
		const now = new Date();
		const diffMs = now.getTime() - date.getTime();
		const diffMins = Math.floor(diffMs / 60000);
		if (diffMins < 1) return "Created just now";
		if (diffMins < 60) return `Created ${diffMins}m ago`;
		const diffHours = Math.floor(diffMins / 60);
		if (diffHours < 24) return `Created ${diffHours}h ago`;
		return `Created ${date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
	});

	const contactDisplay = computed(() => {
		const contacts = sheetData.value?.contacts;
		if (!contacts?.length) return "";
		return getCustomerDisplayNames(contacts);
	});

	const hasAnyDetails = computed(() => {
		if (!sheetData.value) return false;
		return !!(contactDisplay.value || sheetData.value.createdAt || sheetData.value.address);
	});

	type SectionKey = "showDetails" | "showNotes" | "showGallery";

	const toggleableSections = computed(() => [
		{ key: "showNotes" as SectionKey, visibleLabel: "Project Notes", hiddenLabel: "Project Notes", visible: showNotes.value },
		{
			key: "showGallery" as SectionKey,
			visibleLabel: `Selected Media (${mediaItems.value.length})`,
			hiddenLabel: "Selected Media",
			visible: showGallery.value
		}
	]);

	const dragIndex = computed(() => {
		if (!dragState.value.dragId) return -1;
		return mediaItems.value.findIndex((item) => item.id === dragState.value.dragId);
	});

	const dropIndex = computed(() => {
		if (!dragState.value.dropId) return -1;
		let index = mediaItems.value.findIndex((item) => item.id === dragState.value.dropId);
		if (index < 0) return -1;
		if (dragState.value.dropAt === "after") index++;
		return index;
	});

	watch(
		[() => props.dialogVisible, () => props.sheet?.id, () => props.sheet?.accessToken, () => props.projectId],
		async ([visible, sheetId, accessToken], previousValues) => {
			const [previousVisible, previousSheetId, previousAccessToken] = previousValues ?? [];
			const opened = visible && !previousVisible;
			const sheetAccessChanged = sheetId !== previousSheetId || accessToken !== previousAccessToken;

			if (visible && (opened || sheetAccessChanged)) {
				// Component stays mounted across closes; reset so the edit cue shows on reopen or sheet identity/access changes.
				titleFocused.value = false;
				subtitleFocused.value = false;
			}

			if (!visible || !props.sheet) {
				sheetLoadRequest.abortCurrent();
				loadingSheet.value = false;
				// LSP-18065: closing mid-drag must not leave an active drag or a pending
				// drop-target frame behind to act on the next open's media list.
				cancelPendingDropTarget();
				dragState.value = { dragId: null, dropId: null, dropAt: null };
				return;
			}

			await loadSheetData();
		},
		{ immediate: true }
	);

	// LSP-18065: a pending drop-target frame must not outlive the component —
	// a stale callback would scan detached nodes and mutate torn-down state.
	onBeforeUnmount(cancelPendingDropTarget);

	// LSP-18065: becoming view-only mid-drag must abandon the drag — no pending
	// drop-target frame may survive to reorder a now-read-only sheet.
	watch(
		() => props.isViewOnly,
		(viewOnly) => {
			if (!viewOnly) return;
			cancelPendingDropTarget();
			dragState.value = { dragId: null, dropId: null, dropAt: null };
		}
	);

	watch(
		() => mediaPickerVisible.value,
		async (visible) => {
			if (visible) {
				await loadProjectMedia();
			}
		}
	);

	async function loadSheetData() {
		if (!props.sheet?.id || !props.sheet?.accessToken) return;

		const { requestId } = sheetLoadRequest.startRequest();
		loadingSheet.value = true;
		try {
			const [data, project] = await Promise.all([
				api.getSheetPublic(props.sheet.id, props.sheet.accessToken),
				api.getProject(props.projectId)
			]);
			if (!sheetLoadRequest.isCurrent(requestId)) return;

			// Merge project contacts and address into the sheet data
			const contacts = (project?.Customers || []).map((customer: any) => ({
				id: customer.id,
				firstname: customer.firstname,
				lastname: customer.lastname,
				companyName: customer.companyName,
				EmailAddresses: customer.EmailAddresses || [],
				PhoneNumbers: customer.PhoneNumbers || []
			}));
			const geoAddress = project?.GeoAddress;
			data.contacts = contacts;
			data.address = geoAddress
				? {
						formattedAddress: geoAddress.formattedAddress,
						latitude: geoAddress.latitude,
						longitude: geoAddress.longitude
					}
				: null;

			sheetData.value = data;
			sheetName.value = data.name || "";
			sheetSubtitle.value = data.subtitle || "";
			sheetNotes.value = data.notes || "";
			notesUndoStack.length = 0;
			notesRedoStack.length = 0;
			showDetails.value = data.showDetails;
			showNotes.value = data.showNotes;
			showGallery.value = data.showGallery;
			mediaItems.value = [...(data.media || [])];
			savedSnapshot.value = buildSnapshot();
		} catch (err) {
			if (!sheetLoadRequest.isCurrent(requestId)) return;
			notifyError(err, "Could not load this sheet. Please refresh the page and try again.");
			console.error("Failed to load sheet data", err);
			emit("close");
		} finally {
			// Clear loading unless a newer request superseded this one — an aborted-but-not-superseded
			// request must still release the spinner so the dialog cannot stay stuck loading (LSP-15911).
			if (!sheetLoadRequest.isSuperseded(requestId)) {
				loadingSheet.value = false;
			}
		}
	}

	async function loadProjectMedia() {
		loadingProjectMedia.value = true;
		pickerSelected.value = [];
		try {
			const existingMediaIds = new Set(mediaItems.value.map((item) => item.mediaId));
			const [snapshots, recordings] = await Promise.all([
				fetchAllProjectSnapshots(props.projectId),
				api.getRecordingsForProject(props.projectId)
			]);

			availableMedia.value = mapProjectMediaForSheetPicker({
				snapshots,
				recordings,
				existingMediaIds
			});
		} catch (err) {
			notifyError(err, "Could not load this sheet's media. Please refresh the page and try again.");
			console.error("Failed to load project media", err);
		} finally {
			loadingProjectMedia.value = false;
		}
	}

	function toggleSection(section: "showDetails" | "showNotes" | "showGallery") {
		if (section === "showDetails") showDetails.value = !showDetails.value;
		else if (section === "showNotes") showNotes.value = !showNotes.value;
		else if (section === "showGallery") showGallery.value = !showGallery.value;
	}

	function formatDate(dateStr: string): string {
		const date = new Date(dateStr);
		return date.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
	}

	// Drag and drop helpers
	function computeDropPosition(targetMediaId: string, clientX: number, clientY: number, target: HTMLElement): "before" | "after" {
		const rect = target.getBoundingClientRect();
		const relX = (clientX - rect.left) / rect.width - 0.5;
		const relY = (clientY - rect.top) / rect.height - 0.5;

		// Use the dominant axis: Y for cross-row moves, X for same-row moves
		let rawDropAt: "before" | "after";
		if (Math.abs(relY) > Math.abs(relX)) {
			rawDropAt = relY < 0 ? "before" : "after";
		} else {
			rawDropAt = relX < 0 ? "before" : "after";
		}

		// When the raw position would be a no-op on an adjacent item, flip to make it a swap
		const dragIdx = mediaItems.value.findIndex((item) => item.id === dragState.value.dragId);
		const targetIdx = mediaItems.value.findIndex((item) => item.id === targetMediaId);
		if (dragIdx >= 0 && targetIdx >= 0) {
			if (rawDropAt === "before" && dragIdx + 1 === targetIdx) return "after";
			if (rawDropAt === "after" && dragIdx - 1 === targetIdx) return "before";
		}

		return rawDropAt;
	}

	// Resolve the media item closest to the pointer so a drop anywhere in the
	// grid — including the gaps between thumbnails or the empty space past the
	// last item in a row — snaps to the nearest item instead of being ignored
	// (LSP-16639). Nearest-by-center reproduces the prior "over an item"
	// behavior while also covering the surrounding empty space.
	function findNearestMediaItem(clientX: number, clientY: number, grid: HTMLElement): { mediaId: string; element: HTMLElement } | null {
		let nearest: { mediaId: string; element: HTMLElement } | null = null;
		let nearestDistance = Infinity;
		for (const element of grid.querySelectorAll<HTMLElement>(".media-item")) {
			const mediaId = element.dataset.mediaId;
			if (!mediaId) continue;
			const rect = element.getBoundingClientRect();
			const distance = Math.hypot(clientX - (rect.left + rect.width / 2), clientY - (rect.top + rect.height / 2));
			if (distance < nearestDistance) {
				nearestDistance = distance;
				nearest = { mediaId, element };
			}
		}
		return nearest;
	}

	// Drag and drop handlers
	function onDragStart(mediaId: string, event: DragEvent) {
		// View-only sheets render :draggable="false", but the handler stays bound —
		// never let a synthetic drag mutate a read-only sheet.
		if (props.isViewOnly) return;
		const target = (event.target as HTMLElement).closest<HTMLDivElement>(".media-item");
		if (target) {
			const rect = target.getBoundingClientRect();
			event.dataTransfer?.setDragImage(target, event.clientX - rect.left, event.clientY - rect.top);
		}
		event.dataTransfer?.setData("sheet-media-drag", "");
		// A leftover pending pointer from a previous drag must never leak into this one.
		cancelPendingDropTarget();
		dragState.value = { dragId: mediaId, dropId: null, dropAt: null };
	}

	// Resolve the nearest item to the pointer and record it as the drop target.
	// Shared by the mouse (onDragOver) and touch (onTouchMove) paths so the drop
	// semantics live in one place.
	//
	// LSP-18065: dragover/touchmove fire continuously (up to every frame). At ~100
	// attachments, running the nearest-item scan (a getBoundingClientRect per item)
	// AND unconditionally rewriting dragState — which re-patches the entire media
	// grid — on every event froze the page during a drag. Two changes keep it smooth:
	// 1. Coalesce pointer positions to at most one scan per animation frame.
	// 2. Skip the reactive dragState write when the resolved target is unchanged,
	//    so the grid only re-renders when the drop indicator actually moves.
	// (pendingDropPointer / dropUpdateFrame are declared next to dragState above.)
	function applyDropTarget(clientX: number, clientY: number, grid: HTMLElement) {
		const nearest = findNearestMediaItem(clientX, clientY, grid);
		if (!nearest) {
			// LSP-16639 diagnostics: a drag in progress with no resolvable item means
			// the grid rendered no .media-item (shouldn't happen while dragging) — log
			// so a "drop did nothing" re-report can be triaged without reconstruction.
			console.warn(`[SheetEditor] drop target unresolved: no .media-item in grid (dragId=${dragState.value.dragId})`);
			return;
		}
		const dropAt = computeDropPosition(nearest.mediaId, clientX, clientY, nearest.element);
		if (dragState.value.dropId === nearest.mediaId && dragState.value.dropAt === dropAt) return;
		dragState.value = { ...dragState.value, dropId: nearest.mediaId, dropAt };
	}

	function updateDropTarget(clientX: number, clientY: number, grid: HTMLElement) {
		pendingDropPointer = { clientX, clientY, grid };
		if (dropUpdateFrame !== null) return;
		const scheduledFrame = requestAnimationFrame(() => {
			// A stale callback (cancelled or superseded by a newer drag) must not
			// consume the current drag's pending pointer.
			if (dropUpdateFrame !== scheduledFrame) return;
			dropUpdateFrame = null;
			const pending = pendingDropPointer;
			pendingDropPointer = null;
			// The drag may have ended (drop/dragend/touchcancel) before this frame ran.
			if (!pending || !dragState.value.dragId) return;
			applyDropTarget(pending.clientX, pending.clientY, pending.grid);
		});
		dropUpdateFrame = scheduledFrame;
	}

	function cancelPendingDropTarget() {
		if (dropUpdateFrame !== null) {
			cancelAnimationFrame(dropUpdateFrame);
			dropUpdateFrame = null;
		}
		pendingDropPointer = null;
	}

	// A drop can land before the scheduled frame runs — apply the freshest pointer
	// position synchronously so the reorder never uses a stale drop target.
	function flushPendingDropTarget() {
		const pending = pendingDropPointer;
		cancelPendingDropTarget();
		if (!pending || !dragState.value.dragId) return;
		applyDropTarget(pending.clientX, pending.clientY, pending.grid);
	}

	function onDragOver(event: DragEvent) {
		if (!event.dataTransfer?.types.includes("sheet-media-drag") || !dragState.value.dragId) return;
		updateDropTarget(event.clientX, event.clientY, event.currentTarget as HTMLElement);
	}

	function onDrop(event: DragEvent) {
		// Same marker check as onDragOver: a foreign drop (e.g. a file or text
		// dragged from outside) must not flush or consume a sheet-media drag.
		if (!event.dataTransfer?.types.includes("sheet-media-drag")) return;
		flushPendingDropTarget();
		reorderMedia();
	}

	function onDragEnd(mediaId: string) {
		// dragend fires on the drag source; ignore a late one from an item that is
		// no longer the active drag so it cannot wipe a newer drag's state.
		if (dragState.value.dragId !== mediaId) return;
		cancelPendingDropTarget();
		dragState.value = { dragId: null, dropId: null, dropAt: null };
	}

	// Touch handlers
	function onTouchStart(mediaId: string) {
		// The drag handle is v-if'd out in view-only mode, but guard anyway (see onDragStart).
		if (props.isViewOnly) return;
		// A leftover pending pointer from a previous drag must never leak into this one.
		cancelPendingDropTarget();
		dragState.value = { dragId: mediaId, dropId: null, dropAt: null };
	}

	function onTouchMove(event: TouchEvent) {
		if (!dragState.value.dragId) return;
		// Touch events stay targeted at the drag-handle grip, which lives inside the
		// grid — walk up to it. (A template ref can't be used: the grid sits inside
		// the sections v-for, so Vue would collect the ref into an array.)
		const grid = (event.target as HTMLElement).closest<HTMLElement>(".media-grid");
		if (!grid) {
			// LSP-16639 diagnostics: touchmove fired during a drag but the grip wasn't
			// inside a .media-grid — log so a mobile "reorder did nothing" is triagable.
			console.warn(`[SheetEditor] touch reorder: could not resolve .media-grid from touch target (dragId=${dragState.value.dragId})`);
			return;
		}
		const touch = event.touches[0];
		if (!touch) {
			// LSP-18065 hardening: touchmove should always carry at least one touch,
			// but never let a malformed event throw mid-drag.
			console.warn(`[SheetEditor] touch reorder: touchmove carried no touches (dragId=${dragState.value.dragId})`);
			return;
		}
		updateDropTarget(touch.clientX, touch.clientY, grid);
	}

	function onTouchEnd(mediaId: string) {
		// Touch events stay targeted at the grip they started on; ignore a late
		// touchend from an item that is no longer the active drag.
		if (dragState.value.dragId !== mediaId) return;
		flushPendingDropTarget();
		reorderMedia();
		dragState.value = { dragId: null, dropId: null, dropAt: null };
	}

	function onTouchCancel(mediaId: string) {
		if (dragState.value.dragId !== mediaId) return;
		cancelPendingDropTarget();
		dragState.value = { dragId: null, dropId: null, dropAt: null };
	}

	function reorderMedia() {
		const fromIndex = dragIndex.value;
		const toIndex = dropIndex.value;
		// LSP-16639 diagnostics: this no-op outcome is visually identical to the bug
		// this PR fixes (a drop that moves nothing — e.g. the last item dragged into
		// trailing space snaps back to itself). Log which condition matched + indices
		// so a re-report is triagable instead of requiring Datadog archaeology.
		if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex || fromIndex + 1 === toIndex) {
			console.warn(
				`[SheetEditor] media reorder skipped (no-op): fromIndex=${fromIndex} toIndex=${toIndex} dragId=${dragState.value.dragId} dropId=${dragState.value.dropId} dropAt=${dragState.value.dropAt}`
			);
			return;
		}

		const draggedItem = mediaItems.value[fromIndex];
		const newItems = toSplicedCompat(
			toSplicedCompat(mediaItems.value, toIndex, 0, draggedItem),
			fromIndex >= toIndex ? fromIndex + 1 : fromIndex,
			1
		);
		mediaItems.value = newItems;
	}

	function showDropIndicator(itemIndex: number, position: "before" | "after"): boolean {
		const di = dragIndex.value;
		const dri = dropIndex.value;
		if (di < 0 || dri < 0) return false;
		if (position === "before") {
			return dri === itemIndex && itemIndex !== di && itemIndex !== di + 1;
		}
		return dri === itemIndex + 1 && itemIndex + 1 !== di && itemIndex + 1 !== di + 1;
	}

	function removeMedia(index: number) {
		mediaItems.value.splice(index, 1);
	}

	// Media picker
	function togglePickerItem(mediaId: string) {
		const index = pickerSelected.value.indexOf(mediaId);
		if (index >= 0) {
			pickerSelected.value.splice(index, 1);
		} else {
			pickerSelected.value.push(mediaId);
		}
	}

	function addSelectedMedia() {
		const newItems: SheetMediaItem[] = [];
		let nextSortOrder = mediaItems.value.length;

		for (const selectedId of pickerSelected.value) {
			const projectItem = availableMedia.value.find((item) => item.id === selectedId);
			if (!projectItem) continue;
			newItems.push({
				id: `new-${Date.now()}-${nextSortOrder}`,
				mediaId: projectItem.id,
				mediaType: projectItem.mediaType,
				mediaUrl: projectItem.mediaUrl,
				thumbnailUrl: projectItem.thumbnailUrl,
				notes: null,
				createdAt: new Date().toISOString(),
				createdBy: null,
				sortOrder: nextSortOrder
			});
			nextSortOrder++;
		}

		mediaItems.value.push(...newItems);
		mediaPickerVisible.value = false;
	}

	async function validateSheetForm(): Promise<boolean> {
		// Force-validate so legacy data loaded with already-invalid values
		// (e.g. notes > 5000 chars from before the cap shipped) is caught even
		// when `isFormValid` is still `null` because no rule has fired yet.
		if (form.value) {
			const { valid, errors } = await form.value.validate();
			if (!valid) {
				const errorMessage = errors.flatMap((error) => error.errorMessages).join(". ") || "Please fix the errors before saving.";
				toast.error(errorMessage, { timeout: calculateTimeout(errorMessage) });
				return false;
			}
		}
		if (!isSheetNotesWithinLimit(sheetNotes.value)) {
			toast.error(notesTooLongMessage, { timeout: calculateTimeout(notesTooLongMessage) });
			return false;
		}
		return true;
	}

	// `notifySuccess` is false for auto-save before Share — the Share dialog
	// itself is the visible feedback, so a "Sheet saved." toast on top of it
	// is noisy.
	async function persistSheet(notifySuccess: boolean): Promise<boolean> {
		if (!props.sheet?.id) return false;
		if (!(await validateSheetForm())) return false;
		saving.value = true;
		try {
			await api.updateSheet(props.sheet.id, {
				name: sheetName.value || "New Sheet",
				subtitle: sheetSubtitle.value || null,
				notes: sheetNotes.value || null,
				showDetails: showDetails.value,
				showNotes: showNotes.value,
				showGallery: showGallery.value
			});

			const sortedMedia = mediaItems.value.map((item, index) => ({
				id: item.mediaId,
				type: item.mediaType === "snapshot" ? "snapshots" : "recordings",
				sortOrder: index
			}));
			await api.updateSheetMedia(props.sheet.id, sortedMedia);

			savedSnapshot.value = buildSnapshot();
			if (notifySuccess) toast.success("Sheet saved.");
			emit("saved");
			return true;
		} catch (err) {
			notifyError(err, "Could not save the sheet. Please try again.");
			console.error("Failed to save sheet", err);
			return false;
		} finally {
			saving.value = false;
		}
	}

	async function handleSave() {
		if (!isDirty.value) return;
		await persistSheet(true);
	}

	async function handleShareClick() {
		if (saving.value || loadingSheet.value) return;
		if (isDirty.value) {
			const saved = await persistSheet(false);
			if (!saved) return;
		}
		if (!(await validateSheetForm())) return;
		shareDialogVisible.value = true;
	}

	async function handleClose() {
		if (isDirty.value) {
			const result = await (instance?.proxy as any)?.$root?.$confirm?.open(
				UNSAVED_CHANGES_DIALOG_TEXT.title,
				UNSAVED_CHANGES_DIALOG_TEXT.message,
				false,
				UNSAVED_CHANGES_DIALOG_TEXT.agreeText,
				UNSAVED_CHANGES_DIALOG_TEXT.cancelText,
				false,
				false,
				UNSAVED_CHANGES_DIALOG_OPTIONS
			);
			if (result === null || result === undefined) return;
			if (result === true) {
				const saved = await persistSheet(false);
				if (!saved) return;
			}
		}
		savedSnapshot.value = "";
		emit("close");
	}
</script>

<style scoped>
	/* Canvas */
	.sheet-canvas {
		display: flex;
		flex-direction: column;
		gap: 24px;
		padding: 4px;
	}

	/* Card container for title/subtitle/details */
	.sheet-card {
		border: 1px solid #e0e0e0;
		border-radius: 12px;
		padding: 28px 28px 20px;
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	/* Title input */
	.sheet-title-input {
		width: 100%;
		max-width: 100%;
	}
	/* No `min-height: unset` here (or on the subtitle below): Vuetify feeds VTextarea's auto-grown
	   height through --v-input-control-height into `.v-field__input { min-height: max(...) }`, so
	   unsetting it collapses an auto-grow textarea back to one clipped line — the LSP-16763 bug. */
	.sheet-title-input :deep(.v-field__input) {
		font-size: 1.6rem;
		font-weight: 700;
		padding: 0;
		line-height: 1.3;
		letter-spacing: -0.01em;
		/* VTextarea masks the top of its own scroll area so content fades under a floating label:
		   `mask-image: linear-gradient(to bottom, transparent, transparent
		   calc(--v-field-padding-top + --v-input-padding-top - 6px), black calc(same + 4px))`
		   (VTextarea.css). Measured on these fields in Chrome: --v-field-padding-top resolves to 0px
		   (VField.css zeroes it for variant=plain + density=compact) and --v-input-padding-top to 8px,
		   so the fade runs from 2px to 12px down the box. The subtitle's cap-height sits inside that
		   band — font-size 15.2px in a 22.8px line box puts its ascenders around 4-8px — so the
		   placeholder greyed out along its top edge (Jason, review of PR #4328; desktop and iPad).
		   These fields carry no label for content to fade under, so the mask has nothing to do here.
		   -webkit- included because Safari, the reported device, honours the prefixed property.
		   This also drops VTextarea's right-hand scrollbar fade, likewise pointless on a field that
		   auto-grows rather than scrolling until it hits the 300px cap. */
		-webkit-mask-image: none;
		mask-image: none;
	}
	.sheet-title-input :deep(.v-field) {
		padding: 0;
	}
	.sheet-title-edit-affordance {
		opacity: 0.7;
		pointer-events: none;
	}
	.sheet-subtitle-edit-affordance {
		opacity: 0.7;
		pointer-events: none;
	}

	/* Subtitle input */
	.sheet-subtitle-input :deep(.v-field__input) {
		font-size: 0.95rem;
		color: #9e9e9e;
		padding: 0;
		/* Same VTextarea top-fade mask as the title above; see that comment. */
		-webkit-mask-image: none;
		mask-image: none;
	}
	.sheet-subtitle-input :deep(.v-field) {
		padding: 0;
		border-bottom: 1px solid #eeeeee;
		padding-bottom: 12px;
		margin-bottom: 8px;
	}

	/* Details row */
	.details-row {
		display: flex;
		border: 1px solid #e8e8e8;
		border-radius: 10px;
		overflow: visible;
		margin-top: 12px;
		position: relative;
	}

	.details-row.no-data {
		border-style: dashed;
	}

	.details-columns {
		display: flex;
		flex: 1;
		min-width: 0;
	}

	.detail-cell {
		flex: 1;
		padding: 12px 16px;
		min-width: 0;
	}

	.detail-cell + .detail-cell {
		border-left: 1px solid #e8e8e8;
	}

	.detail-label {
		font-size: 0.68rem;
		font-weight: 600;
		letter-spacing: 0.06em;
		color: #9e9e9e;
		margin-bottom: 6px;
	}

	.detail-value {
		font-size: 0.85rem;
		display: flex;
		align-items: center;
		min-width: 0;
	}

	.detail-value .text-truncate {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	@media (max-width: 599px) {
		.details-columns {
			flex-direction: column;
		}

		.detail-cell + .detail-cell {
			border-left: none;
			border-top: 1px solid #e8e8e8;
		}
	}

	@media (max-width: 959px) {
		.detail-label {
			white-space: nowrap;
		}

		.detail-value {
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
	}

	/* Section heading with accent bar */
	.section-heading {
		display: flex;
		align-items: center;
		gap: 10px;
		font-size: 0.95rem;
		font-weight: 700;
		margin-bottom: 12px;
		position: relative;
		padding-left: 0;
	}

	.section-heading.muted {
		opacity: 0.5;
	}

	.section-accent-bar {
		width: 3px;
		height: 18px;
		background: rgb(var(--v-theme-primary));
		border-radius: 2px;
		flex-shrink: 0;
	}

	.section-accent-bar.muted {
		background: #bdbdbd;
	}

	/* Sections wrapper */
	.sheet-section-wrapper {
		position: relative;
		padding: 4px 0;
	}

	.sheet-section-header-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 12px;
	}

	.notes-section {
		padding: 0;
	}

	/* Hidden section placeholder */
	.hidden-section-placeholder {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 12px 16px;
		margin-top: 12px;
		border: 1px dashed #e0e0e0;
		border-radius: 10px;
		cursor: pointer;
		transition: border-color 0.15s ease;
		position: relative;
	}

	.hidden-section-placeholder:hover {
		border-color: rgb(var(--v-theme-primary));
	}

	.hidden-section-inline {
		cursor: pointer;
		padding: 4px 0;
	}

	.hidden-section-inline:hover .section-heading.muted {
		opacity: 0.8;
	}

	.section-visibility-toggle {
		width: 28px;
		height: 28px;
		min-width: 28px;
		flex-shrink: 0;
		transition:
			color 0.15s ease,
			opacity 0.15s ease;
	}

	.section-visibility-toggle.is-visible {
		color: rgb(var(--v-theme-primary));
		opacity: 1;
	}

	.section-visibility-toggle.is-visible:hover {
		opacity: 0.8;
	}

	.section-visibility-toggle.is-hidden {
		color: #8b93a1;
		opacity: 0.85;
	}

	.section-visibility-toggle.is-hidden:hover {
		opacity: 1;
	}

	/* Always show toggle buttons on mobile/touch */
	@media (hover: none) {
		/* Intentionally empty: section visibility toggles are always visible. */
	}

	/* Empty media state */
	.empty-media-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 32px 16px;
		border: 1px dashed #e0e0e0;
		border-radius: 10px;
	}

	/* Media grid */
	.media-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
		gap: 10px;
	}

	.media-item {
		position: relative;
		cursor: grab;
		border-radius: 10px;
		overflow: hidden;
	}

	.media-item:active {
		cursor: grabbing;
	}

	.media-thumb-wrapper {
		position: relative;
		border-radius: 10px;
		overflow: hidden;
		border: 2px solid transparent;
		transition: border-color 0.15s ease;
	}

	.media-item:hover .media-thumb-wrapper {
		border-color: rgb(var(--v-theme-primary));
	}

	.remove-media-btn {
		position: absolute;
		top: 4px;
		right: 4px;
		z-index: 2;
		width: 24px !important;
		height: 24px !important;
		min-width: 24px !important;
		background-color: white !important;
		border: 2px solid black;
		border-radius: 50%;
		color: black;
	}

	.drag-handle {
		position: absolute;
		bottom: 4px;
		left: 4px;
		z-index: 2;
		color: white;
		opacity: 0.8;
		cursor: grab;
		text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
	}

	.video-overlay {
		position: absolute;
		inset: 0;
		pointer-events: none;
	}

	.play-button {
		background-color: #00000066;
		color: #fff;
		border-radius: 50%;
		width: 40px;
		height: 40px;
		font-size: 2rem !important;
		display: flex;
		justify-content: center;
		align-items: center;
	}

	.drop-divider-before {
		position: absolute;
		left: -5px;
		top: 0;
		bottom: 0;
		z-index: 3;
	}

	.drop-divider-after {
		position: absolute;
		right: -5px;
		top: 0;
		bottom: 0;
		z-index: 3;
	}

	/* Media Picker */
	.media-picker-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
		gap: 10px;
	}

	.media-picker-item {
		position: relative;
		cursor: pointer;
		border: 2px solid transparent;
		border-radius: 10px;
		overflow: hidden;
		transition: border-color 0.15s ease;
	}

	.media-picker-item.selected {
		border-color: rgb(var(--v-theme-primary));
	}

	.media-picker-item:hover {
		border-color: rgb(var(--v-theme-primary));
		opacity: 0.92;
	}

	.picker-check {
		position: absolute;
		top: 6px;
		right: 6px;
		background: white;
		border-radius: 50%;
	}
</style>
